// ============================================================================
// DIFF PANEL — story #182
// Shows the addenda diff for a tender document against its declared
// predecessor: new / modified / unchanged / removed requirement rows.
// Reuses RequirementsPanel's row-rendering conventions (BidProjectHome.tsx).
// ============================================================================

import React from 'react';
import { Loader2, GitCompare, AlertTriangle, ArrowRight, Plus, Minus, Equal } from 'lucide-react';
import { useAddendumDiff, useRunAddendumDiff } from '../../hooks/useBid';
import { BidRequirement } from '../../services/bidService';

interface DiffPanelProps {
  projectId: string;
  documentId: string;
  predecessorFileName?: string;
}

const BUCKET_META = {
  new: { label: 'New', icon: Plus, className: 'text-green-700 bg-green-50 border-green-200' },
  modified: { label: 'Modified', icon: AlertTriangle, className: 'text-amber-700 bg-amber-50 border-amber-200' },
  removed: { label: 'Removed', icon: Minus, className: 'text-red-700 bg-red-50 border-red-200' },
  unchanged: { label: 'Unchanged', icon: Equal, className: 'text-slate-500 bg-slate-50 border-slate-200' }
} as const;

function DiffRow({ req, bucket }: { req: BidRequirement; bucket: keyof typeof BUCKET_META }) {
  const meta = BUCKET_META[bucket];
  const Icon = meta.icon;
  return (
    <div className="px-6 py-3 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-xs font-semibold text-navy">
          {req.clauseRef && <span className="text-slate-400 mr-1.5">{req.clauseRef}</span>}
          {req.text.length > 140 ? `${req.text.slice(0, 140)}…` : req.text}
        </p>
        {req.openIssues.length > 0 && (
          <ul className="mt-1 flex flex-wrap gap-1.5">
            {req.openIssues.map((issue, i) => (
              <li key={i} className="flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                <AlertTriangle size={10} /> {issue}
              </li>
            ))}
          </ul>
        )}
      </div>
      <span className={`flex-shrink-0 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 border ${meta.className}`}>
        <Icon size={10} /> {meta.label}
      </span>
    </div>
  );
}

export function DiffPanel({ projectId, documentId, predecessorFileName }: DiffPanelProps) {
  const { data, isLoading } = useAddendumDiff(projectId, documentId, true);
  const runDiffMutation = useRunAddendumDiff(projectId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6 text-slate-500 text-xs bg-slate-50/50">
        <Loader2 size={16} className="animate-spin mr-2" /> Loading diff...
      </div>
    );
  }

  const hasAnyRows = !!data && (data.new.length + data.modified.length + data.unchanged.length + data.removed.length > 0);

  if (!hasAnyRows) {
    return (
      <div className="py-6 flex flex-col items-center gap-2 bg-slate-50/50">
        <p className="text-xs text-slate-400">
          {predecessorFileName ? `No diff run yet against "${predecessorFileName}".` : 'No diff run yet.'}
        </p>
        <button
          onClick={() => runDiffMutation.mutate(documentId)}
          disabled={runDiffMutation.isPending}
          className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-dark border border-primary/30 hover:bg-primary/5 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50"
        >
          {runDiffMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : <GitCompare size={13} />}
          Run diff
        </button>
        {runDiffMutation.isError && (
          <p className="text-xs text-red-600">
            {(runDiffMutation.error as any)?.response?.data?.error || 'Failed to run diff'}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-slate-50/50 divide-y divide-[#e2e8f0]">
      <div className="px-6 py-2.5 flex items-center justify-between bg-white border-b border-[#e2e8f0]">
        <p className="text-[11px] text-slate-500">
          {data!.new.length} new · {data!.modified.length} modified · {data!.unchanged.length} unchanged · {data!.removed.length} removed
        </p>
        <button
          onClick={() => runDiffMutation.mutate(documentId)}
          disabled={runDiffMutation.isPending}
          className="flex items-center gap-1 text-[11px] font-semibold text-primary hover:text-primary-dark transition-colors disabled:opacity-50"
        >
          {runDiffMutation.isPending ? <Loader2 size={11} className="animate-spin" /> : <ArrowRight size={11} />}
          Re-run
        </button>
      </div>
      {data!.modified.map(req => <DiffRow key={req.requirementId} req={req} bucket="modified" />)}
      {data!.new.map(req => <DiffRow key={req.requirementId} req={req} bucket="new" />)}
      {data!.removed.map(req => <DiffRow key={req.requirementId} req={req} bucket="removed" />)}
      {data!.unchanged.map(req => <DiffRow key={req.requirementId} req={req} bucket="unchanged" />)}
    </div>
  );
}
